import { forwardRef, useCallback, useEffect, useState } from 'react';
// Components
import {
  Col,
  Flex,
  Form,
  message,
  Row,
  Select,
  Button,
  Table,
  Typography,
  Tooltip,
} from 'antd';
import StepsHeading from './StepsHeading';
import {
  CustomInput,
  CustomSelect,
  CustomTitle,
  EllipsisTooltip,
} from '../../../components';
import {
  AddProducts,
  GetProductTypes,
  DeleteProduct,
} from '../../../apis/onboarding.js';
import { useSpin } from '../../../components/SpinContext.jsx';
import useToken from '../../../common/helper/useToken';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllProducts } from '../../../redux-toolkit/slice/ProductSlice.js';
//icons
import AddIcon from '../../../assets/icons/add_circle.svg';
import { DeleteIcon, PencilIcon } from '../../../assets/icons/icons';
import { tempratureRangeList } from '../../../common/static_data';
import useUnauthorize from '../../../hooks/useUnauthorize';
import StabilityHandlingTooltip from '@/pages/Shipments/partials/StabilityHandlingTooltip';

const { Option } = Select;
const { Paragraph } = Typography;

const customStyle = {
  height: '36px',
  width: '100%',
  backgroundColor: 'var(--light-gray)',
  color: 'var(--text-secondary)',
  borderRadius: '8px',
  lineHeight: '38px',
  boxShadow: 'none',
  border: '0px',
};

const placeholderStyle = {
  '::placeholder': {
    color: 'var(--text-secondary)',
  },
};

const Products = forwardRef(() => {
  const { showSpin, hideSpin } = useSpin();
  const [productForm] = Form.useForm();
  const name = Form.useWatch('name', productForm);
  const productType = Form.useWatch('product_type', productForm);
  const temperatureRange = Form.useWatch('temperature_range', productForm);
  const isDisabled =
    !name?.trim() || !productType?.trim() || !temperatureRange?.trim();
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const [productTypeList, setProductTypeList] = useState();
  const state = useSelector((state) => state);
  const dispatch = useDispatch();
  const [requestStatus, setRequestStatus] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectKey] = useState(0);
  const [productsList, setProductsList] = useState([]);
  const [productId, setProdcutId] = useState(null);
  const [product, setProduct] = useState(null);

  const [temperatureObject, setTemperatureObject] = useState({
    temperature_max: '',
    temperature_min: '',
    temp_range_is_custom: false,
    temperature_unit: 'C',
  });

  const baseStyle = {
    height: 36,
    width: '100%',
    borderRadius: 8,
    backgroundColor: 'var(--light-gray)',
    border: '0',
    color: 'var(--text-secondary)',
    paddingLeft: 12,
    paddingRight: 12,
    outline: 'none',
  };

  const tableHeader = (text) => (
    <EllipsisTooltip text={text} className='product-table-header-ellipsis' />
  );

  const fetchProductLists = useCallback(() => {
    showSpin();
    GetProductTypes(token)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let res = data.data.data;
            if (res) {
              setProductTypeList(res);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [hideSpin, showSpin, token, unauthorize]);

  useEffect(() => {
    if (state) {
      setProductsList([]);
      setRequestStatus(state?.status ? state.status : null);
      if (state?.Products?.productList?.length > 0) {
        setProductsList(state?.Products?.productList);
      }
    }
  }, [state]);

  useEffect(() => {
    fetchAllProducts(token);
    fetchProductLists();
  }, [fetchProductLists, token]);

  const requirementKeys = ['shock', 'light', 'tilt', 'pressure', 'humidity'];
  const handleEdit = (resp) => {
    setProduct(resp);
    setShowDropdown(false);
    setSelectedRequirements([]);
    setAvailableOptions(['Shock', 'Light', 'Tilt', 'Pressure', 'Humidity']);

    setProdcutId(resp?.id || null);
    productForm.setFieldsValue({
      name: resp.name || '',
      product_type: resp.product_type || '',
      identifier: resp.identifier || '',
      stage_of_development: resp.stage_of_development || '',
      commercial_value: resp.commercial_value || '',
      project_id: resp.project_id || '',
      temperature_range: resp.temperature_range || null,
      viability_window: resp.viability_window || null,
      temperature_min: resp.temperature_min || '',
      temperature_max: resp.temperature_max || '',
      temp_range_is_custom: resp.temp_range_is_custom || false,
      temperature_unit: resp.temperature_unit || '',
      shock: resp.shock ?? '',
      light: resp.light ?? '',
      tilt: resp.tilt ?? '',
      pressure_min: resp.pressure_min ?? '',
      pressure_max: resp.pressure_max ?? '',
      pressure_unit: resp.pressure_unit || '',
      humidity_min: resp.humidity_min ?? '',
      humidity_max: resp.humidity_max ?? '',
    });
    setTemperatureObject({
      temperature_min: resp.temperature_min,
      temperature_max: resp.temperature_max,
      temperature_unit: resp.temperature_unit,
      temp_range_is_custom: resp.temp_range_is_custom,
    });

    const selectedFromBackend = requirementKeys.filter((key) => {
      if (key === 'pressure') {
        return (
          (resp.pressure_min !== undefined &&
            resp.pressure_min !== null &&
            resp.pressure_min !== '') ||
          (resp.pressure_max !== undefined &&
            resp.pressure_max !== null &&
            resp.pressure_max !== '')
        );
      }

      if (key === 'humidity') {
        return (
          (resp.humidity_min !== undefined &&
            resp.humidity_min !== null &&
            resp.humidity_min !== '') ||
          (resp.humidity_max !== undefined &&
            resp.humidity_max !== null &&
            resp.humidity_max !== '')
        );
      }

      return resp[key] !== undefined && resp[key] !== null && resp[key] !== '';
    });

    const selectedCapitalized = selectedFromBackend.map(
      (key) => key.charAt(0).toUpperCase() + key.slice(1)
    );
    setSelectedRequirements(selectedCapitalized);
    setShowDropdown(selectedCapitalized.length === 5);
    setAvailableOptions((prevOptions) =>
      prevOptions.filter((opt) => !selectedCapitalized.includes(opt))
    );

    setIsCustomRange(resp.temp_range_is_custom);
  };

  const allColumns = [
    {
      title: tableHeader('Product name'),
      dataIndex: 'name',
      key: 'name',
      width: '200px',
      render: (text) => (
        <div className='product-table-cell'>
          <EllipsisTooltip
            text={text || '--'}
            className='product-table-ellipsis product-table-ellipsis-bold'
          />
        </div>
      ),
    },
    {
      title: tableHeader('Product type'),
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text) => (
        <div className='product-table-cell'>
          <EllipsisTooltip
            text={text || '--'}
            className='product-table-ellipsis'
          />
        </div>
      ),
    },
    {
      title: tableHeader('Project ID'),
      dataIndex: 'project_id',
      key: 'project_id',
      render: (text) => (
        <div className='product-table-cell'>
          <EllipsisTooltip
            text={text || '--'}
            className='product-table-ellipsis'
          />
        </div>
      ),
    },
    {
      title: tableHeader('Transit viability window'),
      dataIndex: 'viability_window',
      key: 'viability_window',
      condition: (count) => count === 0,
      render: (text) => (
        <div className='product-table-cell'>
          <EllipsisTooltip
            text={text ? `${text} hr` : '--'}
            className='product-table-ellipsis'
          />
        </div>
      ),
    },
    {
      title: tableHeader('Temp. range'),
      dataIndex: 'temperature_range',
      key: 'temperature_range',
      condition: (count) => count === 0,
      render: (_, record) => {
        const minTemp = record.temperature_min || '-';
        const maxTemp = record.temperature_max || '-';
        const range = record.temperature_range || '';

        // Convert to numbers and handle NaN cases
        const _minTempNum =
          minTemp !== null && minTemp !== undefined && !isNaN(Number(minTemp))
            ? Number(minTemp)
            : null;
        const _maxTempNum =
          maxTemp !== null && maxTemp !== undefined && !isNaN(Number(maxTemp))
            ? Number(maxTemp)
            : null;

        const temperatureText = `${Math.round(minTemp)} Â°${
          record?.temperature_unit
        } to ${Math.round(maxTemp)} Â°${record?.temperature_unit}`;

        return (
          <div
            className='product-table-cell'
            title={`${temperatureText} ${range}`}
          >
            <Paragraph
              style={{
                color: 'var(--text-primary)',
                fontSize: '12px',
                fontWeight: '400',
                marginBottom: '0',
              }}
            >
              {range !== 'none' && (
                <div>{`${Math.round(minTemp)} °${
                  record?.temperature_unit
                } to ${Math.round(maxTemp)} °${record?.temperature_unit}`}</div>
              )}
              <div className='product-range-note'>{`${range}`}</div>
            </Paragraph>
          </div>
        );
      },
    },
    {
      title: tableHeader('Requirements'),
      dataIndex: 'requirements',
      key: 'requirements',
      condition: (count) => count > 0,
      render: (_, record) => {
        return (
          <div className='stability-requirement-tooltip'>
            <StabilityHandlingTooltip record={record} />
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: 74,
      align: 'right',
      render: (text, record) => (
        <div className='product-table-actions'>
          <Flex gap={6} justify='flex-end'>
            {record?.permissions?.is_update && (
              <Flex
                justify='center'
                align='center'
                className='product-icon-btn'
                role='button'
                aria-label='Edit'
                onClick={() => handleEdit(record)}
              >
                <PencilIcon color='var(--text-primary)' />
              </Flex>
            )}
            {record?.permissions?.is_delete && (
              <Flex
                justify='center'
                align='center'
                className='product-icon-btn'
                role='button'
                aria-label='Delete'
                onClick={() => handleDelete(record)}
              >
                <DeleteIcon />
              </Flex>
            )}
          </Flex>
        </div>
      ),
    },
  ];

  //Requirement section
  const [currentRequirement, setCurrentRequirement] = useState('');
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([
    'Shock',
    'Light',
    'Tilt',
    'Pressure',
    'Humidity',
  ]);

  useEffect(() => {
    const currentValue = product;
    if (currentRequirement === 'Pressure') {
      productForm.setFieldsValue({
        pressure_min:
          currentValue?.pressure_min ||
          productForm.getFieldValue('pressure_min') ||
          0,
        pressure_max:
          currentValue?.pressure_max ||
          productForm.getFieldValue('pressure_max') ||
          100,
        pressure_unit:
          currentValue?.pressure_unit ||
          productForm.getFieldValue('pressure_unit') ||
          'psi',
      });
    }
    if (currentRequirement === 'Humidity') {
      productForm.setFieldsValue({
        humidity_min:
          currentValue?.humidity_min ||
          productForm.getFieldValue('humidity_min') ||
          0,
        humidity_max:
          currentValue?.humidity_max ||
          productForm.getFieldValue('humidity_max') ||
          100,
      });
    }
  }, [currentRequirement, product, productForm]);

  useEffect(() => {
    if (selectedRequirements?.length > 0) {
      setCurrentRequirement(
        selectedRequirements[selectedRequirements.length - 1]
      );
    }
  }, [selectedRequirements]);

  const renderRequirementField = (type) => {
    const removeItem = (typeToRemove) => {
      setSelectedRequirements((prev) => prev.filter((t) => t !== typeToRemove));
      setAvailableOptions((prev) => [...prev, typeToRemove].sort());
      // Clear form values
      switch (typeToRemove) {
        case 'Shock':
        case 'Light':
        case 'Tilt':
          productForm.setFieldsValue({ [typeToRemove.toLowerCase()]: null });
          break;
        case 'Pressure':
          break;
        case 'Humidity':
          break;
        default:
          break;
      }
    };

    const renderSimpleInput = () => (
      <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
        <Form.Item
          label={type}
          name={type.toLowerCase()}
          style={{ marginBottom: 16 }}
        >
          <div className='requirement-input-style'>
            <Form.Item
              name={type.toLowerCase()}
              rules={[
                {
                  pattern: /^(?:\d{1,6}|\d{0,5}\.\d{1,6})$/,
                  message: 'Enter a valid number (max 6 digits total)',
                },
              ]}
              noStyle
            >
              <CustomInput
                placeholder='Enter value'
                height='36px'
                border='0px'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
                suffixFontWeight='600'
                suffixColor='#000'
                suffix={
                  type === 'Shock' ? 'G' : type === 'Light' ? 'lux' : 'Deg'
                }
              />
            </Form.Item>
            <span
              className='product-row-delete'
              role='button'
              aria-label='Delete'
              onClick={() => removeItem(type)}
            >
              <DeleteIcon />
            </span>
          </div>
        </Form.Item>
      </Col>
    );

    switch (type) {
      case 'Shock':
        return renderSimpleInput('G');
      case 'Light':
        return renderSimpleInput('lux');
      case 'Tilt':
        return renderSimpleInput('Deg');

      case 'Pressure':
        return (
          <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
            <Form.Item label='Pressure' style={{ marginBottom: 16 }}>
              <div className='requirement-input-style'>
                <Form.Item
                  name='pressure_min'
                  dependencies={['pressure_max']}
                  noStyle
                  rules={[
                    {
                      pattern:
                        /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                      message: 'Min value must be between 0 and 100',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const max = getFieldValue('pressure_max');

                        // only run check if min has some value (including 0)
                        if (
                          value !== undefined &&
                          value !== null &&
                          value !== ''
                        ) {
                          if (max === undefined || max === null || max === '') {
                            return Promise.reject(
                              new Error(
                                'Max value is required when Min is entered'
                              )
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CustomInput
                    placeholder='0'
                    height='36px'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
                <span>-</span>
                <Form.Item
                  name='pressure_max'
                  dependencies={['pressure_min']}
                  noStyle
                  rules={[
                    {
                      //pattern: /^(10(\.0{1,4})?|[1-9](\.\d{1,4})?)$/,
                      pattern:
                        /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                      message: 'Max value must be between 0 and 100',
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const min = getFieldValue('pressure_min');

                        if (
                          value !== undefined &&
                          value !== null &&
                          value !== ''
                        ) {
                          if (min === undefined || min === null || min === '') {
                            return Promise.reject(
                              new Error(
                                'Min value is required when Max is entered'
                              )
                            );
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CustomInput
                    placeholder='100'
                    height='36px'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
                <Form.Item name='pressure_unit' noStyle initialValue='psi'>
                  <Select
                    options={[
                      { label: 'psi', value: 'psi' },
                      { label: 'mbar', value: 'mbar' },
                    ]}
                    style={{ ...baseStyle }}
                    dropdownStyle={{ borderRadius: 8 }}
                  />
                </Form.Item>
                <span
                  className='product-row-delete'
                  role='button'
                  aria-label='Delete'
                  onClick={() => removeItem(type)}
                >
                  <DeleteIcon />
                </span>
              </div>
            </Form.Item>
          </Col>
        );

      case 'Humidity':
        return (
          <>
            <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
              <Form.Item label='Humidity' style={{ marginBottom: 16 }}>
                <div className='requirement-input-style'>
                  <Form.Item
                    name='humidity_min'
                    dependencies={['humidity_max']}
                    noStyle
                    rules={[
                      {
                        pattern:
                          /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                        message: 'Min value must be between 0 and 100',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const max = getFieldValue('humidity_max');

                          // only run check if min has some value (including 0)
                          if (
                            value !== undefined &&
                            value !== null &&
                            value !== ''
                          ) {
                            if (
                              max === undefined ||
                              max === null ||
                              max === ''
                            ) {
                              return Promise.reject(
                                new Error(
                                  'Max value is required when Min is entered'
                                )
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <CustomInput
                      placeholder='0'
                      height='36px'
                      border='0px'
                      bgColor='var(--light-gray)'
                      textColor='var(--text-secondary)'
                      borderRadius='8px'
                      //placeholderColor="var(--text-secondary)"
                      lineHeight='38px'
                    />
                  </Form.Item>
                  <span>-</span>
                  <Form.Item
                    name='humidity_max'
                    dependencies={['humidity_min']}
                    noStyle
                    rules={[
                      {
                        pattern:
                          /^(100(\.0{1,4})?|[1-9]?\d(\.\d{1,4})?|0(\.0{1,4})?)$/,
                        message: 'Max value  must be between 0 and 100',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const min = getFieldValue('humidity_min');

                          if (
                            value !== undefined &&
                            value !== null &&
                            value !== ''
                          ) {
                            if (
                              min === undefined ||
                              min === null ||
                              min === ''
                            ) {
                              return Promise.reject(
                                new Error(
                                  'Min value is required when Max is entered'
                                )
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <CustomInput
                      placeholder='100'
                      height='36px'
                      border='0px'
                      bgColor='var(--light-gray)'
                      textColor='var(--text-secondary)'
                      borderRadius='8px'
                      placeholderColor='var(--text-secondary)'
                      lineHeight='38px'
                    />
                  </Form.Item>
                  <Form.Item noStyle>
                    <CustomInput
                      placeholder='%'
                      disabled
                      style={{ ...baseStyle }} // Match Pressure unit width
                    />
                  </Form.Item>
                  <span
                    className='product-row-delete'
                    role='button'
                    aria-label='Delete'
                    onClick={() => removeItem(type)}
                  >
                    <DeleteIcon />
                  </span>
                </div>
              </Form.Item>
            </Col>
          </>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (dispatch) {
      dispatch(fetchAllProducts({ token, is_onboarding: true }));
    }
  }, [dispatch, token]);

  //setting data from store
  useEffect(() => {
    if (state) {
      setRequestStatus(state?.status ? state.status : null);
      if (state?.Products?.productList.length > 0) {
        productForm.setFieldsValue({
          productList: state?.Products?.productList, // 'users' is the name of the Form.List
        });
      }
    }
  }, [productForm, state]);

  //if status is unauthorize
  useEffect(() => {
    if (requestStatus) {
      unauthorize(requestStatus);
    }
  }, [requestStatus, unauthorize]);

  const handleDelete = (deleteData) => {
    if (deleteData && deleteData.id) {
      if (deleteData.id === productId) {
        message.error(
          'This product is in progress. Please complete it before proceeding.'
        );
        return;
      }
      showSpin();
      DeleteProduct(deleteData.id, token)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              dispatch(fetchAllProducts({ token, is_onboarding: true }));
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const handleTemperatureRangeChange = (e) => {
    if (e) {
      if (e === 'custom') {
        productForm.setFieldsValue({
          temp_range_is_custom: true,
          temperature_min: '',
          temperature_max: '',
          temperature_unit: '',
        });
        setIsCustomRange(true);
      } else {
        let obj = tempratureRangeList.find((x) => x.value === e);
        if (obj) {
          setTemperatureObject({
            temperature_min: obj.min,
            temperature_max: obj.max,
            temperature_unit: 'C',
            temp_range_is_custom: false,
          });
          setIsCustomRange(false);
        }
      }
    }
  };

  const submitProducts = (model) => {
    showSpin();
    AddProducts(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            dispatch(fetchAllProducts({ token, is_onboarding: true }));
            setProdcutId(null);
            productForm.resetFields();
            setIsCustomRange(false);
            setShowDropdown(false);
            setSelectedRequirements([]);
            setAvailableOptions([
              'Shock',
              'Light',
              'Tilt',
              'Pressure',
              'Humidity',
            ]);
            message.success(data?.data?.message);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const onFinish = (values) => {
    if (values && Object.keys(values).length > 0) {
      let model = productForm.getFieldsValue();
      if (
        ('humidity_min' in model || 'humidity_max' in model) &&
        (model?.humidity_min == null || model?.humidity_min === '') &&
        (model?.humidity_max == null || model?.humidity_max === '')
      ) {
        message.error(
          'Humidity requirement cannot be left empty. Either remove it or add a value.'
        );
        return;
      }

      if (
        ('pressure_min' in model || 'pressure_max' in model) &&
        (model?.pressure_min == null || model?.pressure_min === '') &&
        (model?.pressure_max == null || model?.pressure_max === '')
      ) {
        message.error(
          'Pressures requirement cannot be left empty. Either remove it or add a value.'
        );
        return;
      }

      model.id = productId ? productId : null;
      if (model.temperature_range === 'custom') {
        model.temp_range_is_custom = true;
      } else {
        model = { ...model, ...temperatureObject };
      }
      if (
        model.stage_of_development &&
        model?.stage_of_development !== 'Commercial'
      ) {
        model.commercial_value = null;
      }
      model.viability_window = !model?.viability_window
        ? null
        : model?.viability_window;
      submitProducts(model);
    }
  };

  return (
    <>
      <div className='onboarding-products-step'>
        <div className='large-steps-content'>
          <StepsHeading
            heading='Tell us about the products you’re shipping'
            text='Let us know the specifications of what you’re shipping so we know when to alert you of temperature excursions and viability risks.'
          />

          <Flex vertical={true} gap='24px'>
            <Form
              form={productForm}
              name='validateOnly'
              layout='vertical'
              onFinish={onFinish}
              initialValues={{ id: null }}
            >
              <div className='product-shipment'>
                <div className='products-form-wrapper'>
                  <div className='left-content-wrapper'>
                    <Row gutter={[16, 16]}>
                      <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          className='form-input-style'
                          label='Name'
                          name={'name'}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter product name',
                            },
                          ]}
                          style={{ marginBottom: '0' }}
                        >
                          <CustomInput
                            placeholder='e.g. Pluvicto'
                            height='36px'
                            border='0px'
                            bgColor='var(--light-gray)'
                            textColor='var(--text-secondary)'
                            borderRadius='8px'
                            placeholderColor='var(--text-secondary)'
                            lineHeight='38px'
                          />
                        </Form.Item>
                      </Col>
                      <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          className='form-input-style'
                          label='Identifier'
                          name={'identifier'}
                          rules={[
                            {
                              type: 'string',
                              min: 3,
                              message:
                                'Product identifier must be at least 3 characters long',
                            },
                            {
                              type: 'string',
                              max: 50,
                              message:
                                'Product identifier cannot exceed 50 characters.',
                            },
                            {
                              whitespace: true,
                              message: 'Only whitespace is not allowed.',
                            },
                          ]}
                          style={{ marginBottom: '0' }}
                        >
                          <CustomInput
                            placeholder='e.g. Pluvicto'
                            height='36px'
                            border='0px'
                            bgColor='var(--light-gray)'
                            textColor='var(--text-secondary)'
                            borderRadius='8px'
                            placeholderColor='var(--text-secondary)'
                            lineHeight='38px'
                          />
                        </Form.Item>
                      </Col>
                      <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          className='form-input-style'
                          label='Product type'
                          name={'product_type'}
                          style={{ marginBottom: '0' }}
                          rules={[
                            {
                              required: true,
                              message: 'Please select one',
                            },
                          ]}
                        >
                          <Select
                            value=''
                            placeholder='Select...'
                            style={{
                              ...customStyle,
                              ...placeholderStyle,
                            }}
                          >
                            {productTypeList &&
                              productTypeList.map((option) => (
                                <Option key={option.id} value={option.id}>
                                  {option.name}
                                </Option>
                              ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row
                      gutter={[16, 16]}
                      style={{ marginTop: '16px', display: 'flex' }}
                    >
                      <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item
                          className='form-input-style'
                          label='Project ID'
                          name={'project_id'}
                          style={{ marginBottom: '0' }}
                          rules={[
                            {
                              max: 20,
                              message: 'Maximum 20 characters allowed',
                            },
                          ]}
                        >
                          <CustomInput
                            placeholder='e.g. Trial-24948'
                            height='36px'
                            border='0px'
                            bgColor='var(--light-gray)'
                            textColor='var(--text-secondary)'
                            borderRadius='8px'
                            placeholderColor='var(--text-secondary)'
                            lineHeight='38px'
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xxl={8}
                        xl={8}
                        lg={12}
                        md={12}
                        sm={24}
                        xs={24}
                        style={{ flexDirection: 'column' }}
                      >
                        <Form.Item
                          className='form-input-style'
                          label='Transit viability window'
                          name={'viability_window'}
                          rules={[
                            {
                              required: false,
                              message: 'Please enter viability window!',
                            },
                            {
                              pattern: /^\d{1,5}$/,
                              message: 'Enter up to 5 digits only!',
                            },
                          ]}
                          style={{ marginBottom: '0' }}
                        >
                          <CustomInput
                            placeholder='5'
                            height='36px'
                            border='0px'
                            bgColor='var(--light-gray)'
                            textColor={'#5E5E5E'}
                            borderRadius='8px'
                            placeholderColor='var(--text-secondary)'
                            lineHeight='38px'
                            suffix='hr'
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xxl={8}
                        xl={8}
                        lg={12}
                        md={12}
                        sm={24}
                        xs={24}
                        style={{ flexDirection: 'column' }}
                      >
                        <Form.Item
                          className='form-input-style'
                          label='Temperature range'
                          name={'temperature_range'}
                          rules={[
                            {
                              required: true,
                              message: 'Please select a temperature range',
                            },
                          ]}
                          style={{ marginBottom: '0' }}
                        >
                          <CustomSelect
                            value='None'
                            placeholder='Select...'
                            onChange={(option) =>
                              handleTemperatureRangeChange(option)
                            }
                            width='100%'
                            height='36px'
                            border='0px'
                            bgColor='var(--light-gray)'
                            textColor='var(--text-secondary)'
                            borderRadius='8px'
                            options={tempratureRangeList}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row
                      gutter={[16, 16]}
                      style={{ marginTop: '16px', display: 'flex' }}
                    >
                      {isCustomRange && (
                        <Col
                          xxl={8}
                          xl={8}
                          lg={12}
                          md={12}
                          sm={24}
                          xs={24}
                          style={{ flexDirection: 'column' }}
                        >
                          <Form.Item
                            className='form-input-style'
                            label='Custom temperature range'
                            style={{ marginBottom: '0' }}
                          >
                            <div className='product-temp-row'>
                              {/* Minimum Temperature Input */}
                              <Form.Item
                                name={'temperature_min'}
                                noStyle
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please specify a minimum value',
                                  },
                                ]}
                                style={{ marginBottom: '0' }}
                              >
                                <CustomInput
                                  placeholder='0'
                                  height='36px'
                                  border='0px'
                                  bgColor='var(--light-gray)'
                                  textColor='var(--text-secondary)'
                                  borderRadius='8px'
                                  placeholderColor='var(--text-secondary)'
                                  lineHeight='38px'
                                />
                              </Form.Item>

                              <CustomTitle
                                text='to'
                                whiteSpace='nowrap'
                                fontSize='14px'
                                fontWeight='500'
                                color='var(--text-primary)'
                                marginBottom={0}
                              />

                              {/* Maximum Temperature Input */}
                              <Form.Item
                                className='form-input-style'
                                name={'temperature_max'}
                                noStyle
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please specify a maximum value',
                                  },
                                ]}
                                style={{ marginBottom: '0' }}
                              >
                                <CustomInput
                                  placeholder='100'
                                  height='36px'
                                  border='0px'
                                  bgColor='var(--light-gray)'
                                  textColor='var(--text-secondary)'
                                  borderRadius='8px'
                                  placeholderColor='var(--text-secondary)'
                                  lineHeight='38px'
                                />
                              </Form.Item>

                              <Form.Item
                                name={['temperature_unit']}
                                noStyle
                                initialValue='C'
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select a unit',
                                  },
                                ]}
                                style={{ marginBottom: '0' }}
                              >
                                <Select
                                  style={{
                                    borderRadius: '8px',
                                    backgroundColor: 'var(--light-gray)',
                                  }}
                                  options={[
                                    { value: 'C', label: '°C' },
                                    { value: 'F', label: '°F' },
                                  ]}
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        </Col>
                      )}

                      {selectedRequirements.map((type, index) =>
                        renderRequirementField(type, index)
                      )}
                      {/* Add Requirement Button or Select */}
                      {!showDropdown && availableOptions.length > 0 && (
                        <Col
                          className='req-btn-wrapper'
                          xxl={8}
                          xl={8}
                          lg={12}
                          md={12}
                          sm={24}
                          xs={24}
                        >
                          <Button
                            className='req-btn'
                            type='dashed'
                            onClick={() => setShowDropdown(true)}
                          >
                            <img
                              className='zl-invert-on-dark req-btn-icon'
                              src={AddIcon}
                              alt='Add'
                            />
                            Add requirement
                          </Button>
                        </Col>
                      )}

                      {showDropdown && availableOptions.length > 0 && (
                        <Col
                          xxl={8}
                          xl={8}
                          style={{ flexDirection: 'column' }}
                          lg={12}
                          md={12}
                          sm={24}
                          xs={24}
                        >
                          <Form.Item label='Add requirement'>
                            <Flex align='center' gap={8}>
                              <Select
                                key={selectKey}
                                style={{
                                  backgroundColor: 'var(--light-gray)',
                                  borderRadius: '8px',
                                  height: '36px',
                                }}
                                placeholder='Select...'
                                onChange={(value) => {
                                  setSelectedRequirements([
                                    ...selectedRequirements,
                                    value,
                                  ]);
                                  setAvailableOptions(
                                    availableOptions.filter(
                                      (item) => item !== value
                                    )
                                  );
                                  setShowDropdown(false);
                                }}
                                options={availableOptions.map((item) => ({
                                  label: item,
                                  value: item,
                                }))}
                              />
                              <span
                                className='product-row-delete'
                                role='button'
                                aria-label='Delete'
                                onClick={() => setShowDropdown(false)}
                              >
                                <DeleteIcon />
                              </span>
                            </Flex>
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </div>

                  <div className='right-content-wrapper'>
                    {isDisabled ? (
                      <Tooltip title='Fill in all required fields(*) to enable the button'>
                        <Button
                          className='p-submit-btn'
                          htmlType='submit'
                          type='primary'
                          disabled
                        >
                          Add Product
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        className='p-submit-btn'
                        htmlType='submit'
                        type='primary'
                        onClick={() => setShowDropdown(true)}
                      >
                        Add Product
                      </Button>
                    )}
                  </div>
                </div>

                {/* Listing Style */}
                <div className='settings-content-wrapper p-table-wrapper'>
                  <CustomTitle
                    text={`Products (${productsList?.length})`}
                    fontSize={20}
                    fontWeight='700'
                    lineHeight='16px'
                    color='var(--text-primary)'
                    marginBottom={'0px'}
                  />
                  <Table
                    columns={allColumns}
                    dataSource={productsList}
                    pagination={false}
                    className='settings-table'
                    scroll={{ x: 900 }}
                    tableLayout='fixed'
                    rowHoverable={false}
                  />
                </div>
              </div>
            </Form>
          </Flex>
        </div>
      </div>
    </>
  );
});

Products.displayName = 'Products';
export default Products;
